import type { FC } from "react";
import { useCallback } from "react";

import { ChevronBoldBackwardIcon } from "@jti/brand-icons/chevron-bold-backward";
import { redirectToCartPage } from "@jti/store/utils";
import type { Translate } from "@jti/ui";
import { useTranslations } from "@jti/ui";

import { CheckOutSteps } from "@checkout/constants/checkout-constants";

import styles from "./HeaderWithBreadcrumbs.module.scss";

type HeaderWithBreadcrumbsProps = {
  title?: string;
  subtitle?: string;
  isFreeTrial?: boolean;
  disableBackButton?: boolean;
  step?: CheckOutSteps; // TODO: shold be abandoned and disableBackButton used instead
};

const getRenderLinks = (translate: Translate) => [
  { label: translate("cart.checkout.shipping_label"), key: CheckOutSteps.Shipping },
  { label: translate("cart.checkout.delivery_label"), key: CheckOutSteps.Delivery },
  { label: translate("cart.checkout.payment_label"), key: CheckOutSteps.Payment },
];

export const HeaderWithBreadcrumbs: FC<HeaderWithBreadcrumbsProps> = ({
  title,
  subtitle,
  disableBackButton = false,
  isFreeTrial = false,
  step,
}) => {
  const { __ } = useTranslations();

  const handleCheckoutClick = useCallback(() => {
    redirectToCartPage();
  }, []);

  const breadcrumbLinks = isFreeTrial
    ? [{ label: __("cart.checkout.free_trial_label"), key: CheckOutSteps.FreeTrial }, ...getRenderLinks(__)]
    : getRenderLinks(__);

  return (
    <>
      <div className={styles.container}>
        {!isFreeTrial && (
          <button
            data-testid="goBack"
            disabled={disableBackButton}
            className={styles.btnGoBack}
            onClick={handleCheckoutClick}
          >
            <ChevronBoldBackwardIcon width="12px" height="12px" />
            {__("cart.checkout.go_back_label")}
          </button>
        )}

        <div className={styles.headerLink}>
          {breadcrumbLinks.map(({ key, label }) => (
            <span
              key={key}
              className={`${key === step ? styles.selected : ""}`}
              data-testid={key === step ? `breadcrumb-links-selected-element` : undefined}
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {(title || subtitle) && (
        <div className={styles.title}>
          {title && <div data-testid="page-base-title">{title}</div>}
          {subtitle && <span>{subtitle}</span>}
        </div>
      )}
    </>
  );
};
